/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  Box,
  Button,
  Flex,
  Link,
  Stack,
  Text,
  ToastId,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { formatValue } from 'components/Balance/PriceLabel';
import useWalletBalance from 'hooks/useWalletBalance';

import {
  IconDollarBalance,
  IconMatic,
  IconRacinoLogo,
  IconUpgradeNew,
  IconUsdcBig,
} from 'icons';
import { useEffect, useRef, useState } from 'react';
import RToast from 'components/Toast/Toast';
import { useCryptoConvervtion } from 'hooks/Crypto/useCryptoConvertion';
import { useWallet, useCheckout } from 'context';
import { useGetTuneUpPrices } from 'hooks';
import IconMaticBig from 'components/IconMaticBig/IconMaticBig';
import { InsufficientFunds } from '../InsufficientFunds';
import { FundsProvider } from 'context/fundsContext';

const tokenList = {
  usdc: {
    currency: 'USDC',
    icon: IconDollarBalance,
    color: 'usdc.100',
  },
  matic: {
    currency: 'MATIC',
    icon: IconMatic,
    color: 'matic.100',
  },
};

type CryptoTabTuneUpProps = {
  onSuccess: (actionId: string) => void;
};

export const CryptoTabTuneUp = ({ onSuccess }: CryptoTabTuneUpProps) => {
  const { data: balance } = useWalletBalance();
  const { data: tuneUpPrices } = useGetTuneUpPrices();
  const userBalance = balance?.getUserBalance;
  const tuneUpPrice = tuneUpPrices?.getTuneUpsPrices;

  const { wallet } = useWallet();
  const { setIsLoadingPurchase } = useCheckout();
  const [selectedCrypto] = useState<'matic' | 'usdc'>(
    'usdc'
  );

  const [cryptoPrice, setCryptoPrice] = useState<{
    price: number;
    readablePrice: number;
  }>({ price: 0, readablePrice: 0 });

  const [usdPrice, setUsdPrice] = useState<number>(0);

  const { data: convertion } = useCryptoConvervtion(selectedCrypto);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (selectedCrypto === 'usdc') {
      setCryptoPrice({
        price: tuneUpPrice?.usdcPrice || 0,
        readablePrice: tuneUpPrice?.readableUsdcPrice || 0,
      });
    } else {
      setCryptoPrice({
        price: tuneUpPrice?.maticPrice || 0,
        readablePrice: tuneUpPrice?.readableMaticPrice || 0,
      });
    }
  }, [tuneUpPrice, selectedCrypto]);

  useEffect(() => {
    // convert crypto to usd
    if (convertion?.last) {
      const usdPrice = cryptoPrice.readablePrice * Number(convertion?.last);
      setUsdPrice(usdPrice);
    }
  }, [cryptoPrice, convertion, selectedCrypto]);

  const toast = useToast();
  const toastIdRef = useRef<ToastId | null>(null);
  const sendToast = (variant: string, text: string) => {
    toastIdRef.current = toast({
      position: 'top',
      render: () => (
        <RToast
          variant={variant as 'success' | 'error' | 'warning' | 'info'}
          title={text}
          onClose={() => toast.close(toastIdRef.current as ToastId)}
        />
      ),
    });
  };

  const handleCryptoPayment = async () => {
    setIsLoading(true);
    console.log(`Init Crypto payment with: ${selectedCrypto}`);

    if (!wallet) {
      sendToast('error', 'Wallet not connected');
      return;
    }

    switch (selectedCrypto) {
      case 'usdc':
        await handleUsdcPayment();
        break;
    }
    setIsLoading(false);
  };

  const handleUsdcPayment = async () => {
    if (!wallet) {
      return;
    }

    console.log(userBalance?.usdc, tuneUpPrice?.usdcPrice);

    if (userBalance?.usdc! < tuneUpPrice?.usdcPrice! / 1e20) {
      sendToast('error', 'Insufficient balance');
      return;
    }

    const { approveUsdc } = await import('utils/crossmint/usdc');
    console.log('APPROVE USDC CALLED');
    const approval = await approveUsdc({
      aaWallet: wallet,
      spenderAddress: process.env.NEXT_PUBLIC_TUNE_UP_CONTRACT_ADDRESS || '',
    });
    console.log('APPROVE RESULT: ', approval);

    try {
      setIsLoadingPurchase(true);
      const { buyWithUsdc } = await import('utils/crossmint/buy-tuneup');
      const payment: any = await buyWithUsdc({
        aaWallet: wallet,
      });
      console.log(payment);
      setIsLoadingPurchase(false);
      onSuccess(payment);
      sendToast('success', 'Payment successful');
    } catch (error) {
      console.log(error);
      setIsLoadingPurchase(false);
      sendToast('error', 'Payment failed');
    }
  };

  const convertedPrice = (price: number) => {
    if (selectedCrypto === 'usdc') return price / 1e26;
    return price / 1e18;
  };
  const convertedPriceBalance = (price: number) => {
    if (selectedCrypto === 'usdc') return price / 1e6;

    return price / 1e18;
  };


  const isInsufficientFunds =
    !userBalance?.usdc ||
    userBalance.usdc < (tuneUpPrice?.usdcPrice || 0) / 1e20;

  return (
    <Flex h="100%" flexDir="column" gap="1rem">
      <FundsProvider>
        <Stack w="full" gap="2rem" align="center">
          <VStack
            alignItems="center"
            w="full"
            justifyContent="space-between"
            background="linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 100%)"
            borderRadius="4px"
            filter="auto"
            backdropFilter="blur(20px)"
            p="3"
            position="relative"
            zIndex={1}
            gap="1rem"
            _before={{
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: '4px',
              background:
                'linear-gradient(90deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)',
              padding: '2px',
              mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
              maskComposite: 'exclude',
            }}
          >
            <Box
              position="absolute"
              inset={0}
              w="full"
              h="full"
              background="linear-gradient(0deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)"
              opacity={0.5}
              borderRadius="4px"
            />
            <Flex w="full" justifyContent="space-between">
              <Flex gap={2} alignItems="center">
                {selectedCrypto === 'usdc' ? (
                  <IconUsdcBig
                    width={{ base: '1rem', md: '1.5rem' }}
                    height={{ base: '1rem', md: '1.5rem' }}
                  />
                ) : (
                  <IconMaticBig
                    width={{ base: '1rem', md: '1.5rem' }}
                    height={{ base: '1rem', md: '1.5rem' }}
                  />
                )}

                <Text
                  fontSize={{ base: '0.875rem', md: '1.125rem' }}
                  lineHeight={{ base: '1.125rem', md: '1.5rem' }}
                  fontWeight={600}
                  color="white.80"
                  textTransform="uppercase"
                >
                  {selectedCrypto}
                </Text>
              </Flex>

              <Flex alignItems="center" gap={1}>
                <Text
                  fontSize={{ base: '0.875rem', md: '1.125rem' }}
                  lineHeight={{ base: '1.125rem', md: '1.5rem' }}
                  fontWeight={600}
                  color="white.80"
                >
                  {formatValue(
                    convertedPriceBalance(userBalance?.[selectedCrypto] || 0)
                  )}
                </Text>

                <Text
                  fontSize={{ base: '0.75rem', md: '0.875rem' }}
                  lineHeight={{ base: '0.875rem', md: '1.125rem' }}
                  fontWeight={600}
                  color={tokenList[selectedCrypto].color}
                >
                  {tokenList[selectedCrypto].currency}
                </Text>
              </Flex>
            </Flex>

            {isInsufficientFunds && <InsufficientFunds />}
          </VStack>

          <Flex
            alignItems="center"
            w="full"
            justifyContent="space-between"
            background="linear-gradient(90deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%)"
            borderRadius="4px"
            backdropFilter="auto"
            backdropBlur="4px"
            p="3"
            position="relative"
            zIndex={0}
            _before={{
              content: '""',
              position: 'absolute',
              inset: 0,
              padding: '2px',
              borderRadius: '4px',
              background:
                'linear-gradient(90deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%)',
              mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
              maskComposite: 'exclude',
            }}
          >
            <Flex alignItems="center" gap={2}>
              <IconUpgradeNew
                width="1.5rem"
                height="1.5rem"
                __css={{
                  path: {
                    fill: 'white.80',
                  },
                }}
              />

              <Text
                fontSize={{ base: '0.875rem', md: '1.125rem' }}
                lineHeight={{ base: '1.125rem', md: '1.5rem' }}
                fontWeight={600}
                color="white.80"
              >
                Tune Up Cost
              </Text>
            </Flex>

            <Flex alignItems="center" gap={1}>
              <Text
                fontSize={{ base: '0.875rem', md: '1.125rem' }}
                lineHeight={{ base: '1.125rem', md: '1.5rem' }}
                fontWeight={600}
                color="white.80"
              >
                {formatValue(
                  convertedPrice(cryptoPrice.price).toFixed(4).toString()
                )}
              </Text>

              <Text
                fontSize={{ base: '0.75rem', md: '0.875rem' }}
                lineHeight={{ base: '0.875rem', md: '1.125rem' }}
                fontWeight={600}
                color={tokenList[selectedCrypto].color}
              >
                {tokenList[selectedCrypto].currency}
              </Text>
            </Flex>
          </Flex>
        </Stack>

        <Stack>
          <Button
            variant="crossmint"
            w="full"
            onClick={handleCryptoPayment}
            isLoading={isLoading}
            isDisabled={isInsufficientFunds}
            fontSize={{ base: '0.75rem', md: '1rem' }}
            lineHeight={{ base: '1rem', md: '140%' }}
            fontWeight={400}
            textTransform="none"
          >
            Pay ${formatValue(convertedPrice(cryptoPrice.price).toFixed(2).toString())}
          </Button>

          <Flex
            textAlign="center"
            fontSize="0.75rem"
            lineHeight="0.875rem"
            fontWeight={500}
            color="white.60"
            alignItems="center"
            justifyContent="center"
            gap="0.25rem"
          >
            By continuing, you accept{' '}
            <IconRacinoLogo width="1rem" height="1rem" />{' '}
            <Link
              color="bloodMoon.100"
              href="https://racino.io/terms-and-conditions"
              isExternal
            >
              Racino&apos;s terms
            </Link>
          </Flex>
        </Stack>
      </FundsProvider>
    </Flex>
  );
};
